import React from 'react';
import { Globe, Brain, Share2, ArrowRight, CheckCircle, Zap, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const services = [
    {
      icon: <Globe size={40} />,
      title: 'Website Development',
      description: 'Custom, responsive websites built with modern technologies. From sleek landing pages to complex web applications, we create digital experiences that convert visitors into customers.',
      features: ['Responsive Design', 'SEO Optimized', 'Fast Performance', 'Secure & Scalable'],
      gradient: 'linear-gradient(135deg, rgba(51, 204, 204, 0.1) 0%, rgba(218, 229, 239, 0.15) 100%)'
    },
    {
      icon: <Brain size={40} />,
      title: 'AI Marketing Services',
      description: 'Smart automation and analytics for business growth. Leverage artificial intelligence to optimize your marketing campaigns, predict customer behavior, and maximize ROI.',
      features: ['Marketing Automation', 'Predictive Analytics', 'Customer Insights', 'ROI Optimization'],
      gradient: 'linear-gradient(135deg, rgba(36, 67, 89, 0.08) 0%, rgba(218, 229, 239, 0.12) 100%)'
    },
    {
      icon: <Share2 size={40} />,
      title: 'Social Media Growth',
      description: 'Build engagement and visibility online. Strategic social media management that grows your audience, increases brand awareness, and drives meaningful engagement.',
      features: ['Content Strategy', 'Community Management', 'Analytics & Reporting', 'Brand Building'],
      gradient: 'linear-gradient(135deg, rgba(51, 204, 204, 0.08) 0%, rgba(218, 229, 239, 0.18) 100%)'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Discovery',
      description: 'Understanding your business goals and requirements'
    },
    {
      number: '02',
      title: 'Strategy',
      description: 'Creating a customized plan for your success'
    },
    {
      number: '03',
      title: 'Execution',
      description: 'Implementing solutions with precision'
    },
    {
      number: '04',
      title: 'Growth',
      description: 'Monitoring and optimizing for continued success'
    }
  ];

  const benefits = [
    {
      icon: <Zap size={24} />,
      text: 'Fast turnaround times'
    },
    {
      icon: <CheckCircle size={24} />,
      text: 'Quality guaranteed'
    },
    {
      icon: <Clock size={24} />,
      text: '24/7 support available'
    }
  ];

  return (
    <section id="services" style={{ background: 'var(--bg-section)', padding: '7rem 1.5rem 5rem' }}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem', maxWidth: '800px', margin: '0 auto 4rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            Our Services
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Comprehensive digital solutions tailored to your business needs
          </p>
        </div>

        {/* Benefits Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          flexWrap: 'wrap',
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '4rem',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
        }}>
          {benefits.map((benefit, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ color: 'var(--accent-text)' }}>
                {benefit.icon}
              </div>
              <span className="body-medium" style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                {benefit.text}
              </span>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem', marginBottom: '5rem' }}>
          {services.map((service, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                border: '2px solid var(--border-light)',
                borderRadius: '20px',
                padding: '2.5rem',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(51, 204, 204, 0.2)';
                e.currentTarget.style.borderColor = 'var(--accent-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border-light)';
              }}
            >
              {/* Gradient background overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '200px',
                background: service.gradient,
                borderRadius: '20px 20px 0 0',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '16px',
                    background: 'var(--gradient-button)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    marginBottom: '1.5rem',
                    boxShadow: '0 8px 20px rgba(51, 204, 204, 0.3)'
                  }}
                >
                  {service.icon}
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                  {service.title}
                </h3>
                
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', flexGrow: 1 }}>
                  {service.description}
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  {service.features.map((feature, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <CheckCircle size={18} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                      <span className="body-small" style={{ color: 'var(--text-body)', fontWeight: '500' }}>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link
                  to="/contact"
                  style={{
                    color: 'var(--accent-text)',
                    textDecoration: 'none',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '1rem',
                    transition: 'gap 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.gap = '0.75rem';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.gap = '0.5rem';
                  }}
                >
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Our Process Section */}
        <div style={{ background: 'white', borderRadius: '24px', padding: '4rem 2rem', position: 'relative', overflow: 'hidden' }}>
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(51, 204, 204, 0.05) 0%, transparent 70%)',
            zIndex: 0
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h3 className="heading-2" style={{ marginBottom: '1rem', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                Our Process
              </h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                A proven approach to delivering exceptional results
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
              {process.map((step, index) => (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    padding: '2rem 1.5rem',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'var(--gradient-button)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem',
                      fontSize: '1.75rem',
                      fontWeight: '700',
                      color: 'white',
                      boxShadow: '0 8px 20px rgba(51, 204, 204, 0.3)'
                    }}
                  >
                    {step.number}
                  </div>
                  <h4 style={{ fontWeight: '600', fontSize: '1.25rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                    {step.title}
                  </h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;